/**
 * Pure helpers for phone-first collect-before-pay + COLLECT_METADATA_REQUIRED bounce.
 * Extracted for behavioral unit tests (G3) — hook wires these with injected deps.
 */
import type { SessionMetadataCheckoutMode, SessionMetadataCollect } from 'pi-kiosk-shared';
import type { CheckoutSessionDTO, CheckoutSubMode } from '../../checkout/checkoutApi.js';
import { isUsableCheckoutSessionCollect } from '../../shop/presenters/shopCheckoutPresenter.js';
import type { PickupSlotSelection } from '../../checkout/PickupSlotPicker.js';
import { ApiError } from '../../../shared/api/ApiClient.js';
import type { ProductCheckoutStep } from '../../shop/presenters/shopCheckoutPresenter.js';

export type PersistCollectBounceReason =
  | 'collectUnusableWhileModeLocked'
  | 'collectRequiredBeforePay'
  | 'collectLaterSlotRequired';

export type PersistCollectBeforePayGate =
  | { readonly kind: 'allow-without-patch' }
  | { readonly kind: 'bounce-to-collect'; readonly reason: PersistCollectBounceReason }
  | {
      readonly kind: 'patch';
      readonly collectPayload: SessionMetadataCollect;
      readonly mode: CheckoutSubMode;
    };

export function resolvePersistCollectBeforePayGate(input: {
  readonly activeSession: CheckoutSessionDTO | null;
  readonly collectPayloadForPersist: SessionMetadataCollect | null;
  readonly selectedCheckoutMode: CheckoutSubMode | null;
  readonly effectivePickupSlot: PickupSlotSelection | null;
}): PersistCollectBeforePayGate {
  const activeSession = input.activeSession;
  if (activeSession?.purposeType === 'DONATION') {
    return { kind: 'allow-without-patch' };
  }
  if (
    activeSession?.status === 'AWAITING_PAYMENT' ||
    activeSession?.checkoutModeEditable === false
  ) {
    if (!isUsableCheckoutSessionCollect(activeSession?.collect ?? null)) {
      return { kind: 'bounce-to-collect', reason: 'collectUnusableWhileModeLocked' };
    }
    return { kind: 'allow-without-patch' };
  }
  if (isUsableCheckoutSessionCollect(activeSession?.collect ?? null)) {
    return { kind: 'allow-without-patch' };
  }
  const mode =
    input.selectedCheckoutMode ??
    activeSession?.checkoutSubMode ??
    activeSession?.checkoutMode?.selectedMode ??
    null;
  const collectPayload =
    input.collectPayloadForPersist ?? activeSession?.collect ?? null;
  if (collectPayload === null || mode === null || activeSession === null) {
    return { kind: 'bounce-to-collect', reason: 'collectRequiredBeforePay' };
  }
  if (
    collectPayload.timing === 'LATER' &&
    (collectPayload.pickup?.promisedPickupAt == null ||
      collectPayload.pickup.promisedPickupAt.trim().length === 0) &&
    input.effectivePickupSlot === null
  ) {
    return { kind: 'bounce-to-collect', reason: 'collectLaterSlotRequired' };
  }
  return { kind: 'patch', collectPayload, mode };
}

export function applyBounceToCollectForMetadataRequired(input: {
  readonly sessionId: string | null;
  /** Live session collect — drives force mark/clear on bounce (G2). */
  readonly sessionCollect: SessionMetadataCollect | null | undefined;
  readonly writeCheckoutInFlowStep: (sessionId: string, step: 'collect') => void;
  readonly setCheckoutStep: (step: ProductCheckoutStep) => void;
  readonly markCheckoutFalseCollectForce: (sessionId: string) => void;
  readonly clearCheckoutFalseCollectForce: (sessionId: string) => void;
  readonly showPaymentError: (
    message: string,
    analyticsSource: string,
    err?: unknown,
  ) => void;
  readonly message: string;
  readonly analyticsSource: string;
  readonly err?: unknown;
}): void {
  if (input.sessionId !== null) {
    input.writeCheckoutInFlowStep(input.sessionId, 'collect');
    if (isUsableCheckoutSessionCollect(input.sessionCollect ?? null)) {
      input.clearCheckoutFalseCollectForce(input.sessionId);
    } else {
      input.markCheckoutFalseCollectForce(input.sessionId);
    }
  }
  input.setCheckoutStep('collect');
  input.showPaymentError(input.message, input.analyticsSource, input.err);
}

/** Classify payment-start / unpaid-reuse catch for collect bounce vs other handling. */
export function isCollectMetadataRequiredError(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'COLLECT_METADATA_REQUIRED';
}

/**
 * Primary start QR + unpaid-reuse Show QR share this: on COLLECT_METADATA_REQUIRED,
 * bounce to collect and return true (caller should abort payment start).
 */
export function tryBounceOnCollectMetadataRequired(
  err: unknown,
  bounce: (analyticsSource: string, err?: unknown) => void,
  analyticsSource: string,
): boolean {
  if (!isCollectMetadataRequiredError(err)) {
    return false;
  }
  bounce(analyticsSource, err);
  return true;
}

export type RunPersistCollectBeforePayInput = {
  readonly sid: string;
  readonly activeSession: CheckoutSessionDTO | null;
  readonly collectPayloadForPersist: SessionMetadataCollect | null;
  readonly selectedCheckoutMode: CheckoutSubMode | null;
  readonly effectivePickupSlot: PickupSlotSelection | null;
  readonly sessionIdFromUrl: string | null;
  readonly writeCheckoutInFlowStep: (sessionId: string, step: 'collect') => void;
  readonly setCheckoutStep: (step: ProductCheckoutStep) => void;
  readonly markCheckoutFalseCollectForce: (sessionId: string) => void;
  readonly clearCheckoutFalseCollectForce: (sessionId: string) => void;
  readonly showPaymentError: (
    message: string,
    analyticsSource: string,
    err?: unknown,
  ) => void;
  readonly collectMetadataRequiredMessage: string;
  readonly updateCollectMutateAsync: (input: {
    readonly sessionId: string;
    readonly collect: SessionMetadataCollect;
    readonly checkoutMode: SessionMetadataCheckoutMode;
  }) => Promise<unknown>;
  readonly buildCheckoutMode: (mode: CheckoutSubMode) => SessionMetadataCheckoutMode;
  readonly rememberCheckoutModeIfNeeded: (
    activeSession: CheckoutSessionDTO,
    mode: CheckoutSubMode,
  ) => void;
};

/**
 * Gate + optional PATCH. Returns success without calling mutate when session
 * collect is already usable. Bounce reasons land on collect step.
 * Mutate errors are rethrown for the hook catch (invalid-state, locked, etc.).
 */
export async function runPersistCollectBeforePay(
  input: RunPersistCollectBeforePayInput,
): Promise<boolean> {
  const gate = resolvePersistCollectBeforePayGate({
    activeSession: input.activeSession,
    collectPayloadForPersist: input.collectPayloadForPersist,
    selectedCheckoutMode: input.selectedCheckoutMode,
    effectivePickupSlot: input.effectivePickupSlot,
  });
  if (gate.kind === 'allow-without-patch') {
    return true;
  }
  if (gate.kind === 'bounce-to-collect') {
    applyBounceToCollectForMetadataRequired({
      sessionId: input.sessionIdFromUrl,
      sessionCollect: input.activeSession?.collect,
      writeCheckoutInFlowStep: input.writeCheckoutInFlowStep,
      setCheckoutStep: input.setCheckoutStep,
      markCheckoutFalseCollectForce: input.markCheckoutFalseCollectForce,
      clearCheckoutFalseCollectForce: input.clearCheckoutFalseCollectForce,
      showPaymentError: input.showPaymentError,
      message: input.collectMetadataRequiredMessage,
      analyticsSource: gate.reason,
    });
    return false;
  }
  const { collectPayload, mode } = gate;
  const checkoutMode = input.buildCheckoutMode(mode);
  await input.updateCollectMutateAsync({
    sessionId: input.sid,
    collect: collectPayload,
    checkoutMode,
  });
  if (input.activeSession !== null) {
    input.rememberCheckoutModeIfNeeded(input.activeSession, mode);
  }
  return true;
}
